import { Directive, ElementRef, inject, Input, NgZone, OnChanges, OnDestroy, SimpleChanges } from '@angular/core'

const FLIP_KEY_ATTRIBUTE = 'data-flip-key'
const ENTER_TRANSLATE_Y = '20%'

interface FlipRect {
  x: number
  y: number
  width: number
  height: number
}

interface FlipSnapshot {
  layoutX: number
  layoutY: number
  visual: FlipRect
}

@Directive({
  selector: '[gbFlipList]',
  standalone: true,
})
export class GbFlipListDirective implements OnChanges, OnDestroy {
  @Input() gbFlipList: unknown[] | null = null
  @Input() gbFlipDuration = 400

  private pendingFrame: number | null = null

  private el = inject<ElementRef<HTMLElement>>(ElementRef)
  private ngZone = inject(NgZone)

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['gbFlipList'] || changes['gbFlipList'].firstChange) return
    if (typeof Element.prototype.animate !== 'function') return

    this.ensureContainerPositioned()
    const previousRects = this.snapshotRects()
    const previousElements = this.keyedElements()

    this.ngZone.runOutsideAngular(() => {
      if (this.pendingFrame !== null) {
        cancelAnimationFrame(this.pendingFrame)
      }
      this.pendingFrame = requestAnimationFrame(() => {
        this.pendingFrame = null
        this.playAnimations(previousRects, previousElements)
      })
    })
  }

  ngOnDestroy(): void {
    if (this.pendingFrame !== null) {
      cancelAnimationFrame(this.pendingFrame)
    }
  }

  private keyedElements(): Map<string, HTMLElement> {
    const elements = new Map<string, HTMLElement>()
    const children = this.el.nativeElement.querySelectorAll<HTMLElement>(`[${FLIP_KEY_ATTRIBUTE}]`)
    children.forEach((child) => {
      const key = child.getAttribute(FLIP_KEY_ATTRIBUTE)
      if (key) {
        elements.set(key, child)
      }
    })
    return elements
  }

  private ensureContainerPositioned(): void {
    const container = this.el.nativeElement
    if (getComputedStyle(container).position === 'static') {
      container.style.position = 'relative'
    }
  }

  private snapshotRects(): Map<string, FlipSnapshot> {
    const container = this.el.nativeElement
    const containerRect = container.getBoundingClientRect()
    const scale = container.offsetWidth ? containerRect.width / container.offsetWidth : 1

    const rects = new Map<string, FlipSnapshot>()
    this.keyedElements().forEach((element, key) => {
      const rect = element.getBoundingClientRect()
      rects.set(key, {
        layoutX: element.offsetLeft,
        layoutY: element.offsetTop,
        visual: {
          x: (rect.left - containerRect.left) / scale,
          y: (rect.top - containerRect.top) / scale,
          width: rect.width / scale,
          height: rect.height / scale,
        },
      })
    })
    return rects
  }

  private playAnimations(previousRects: Map<string, FlipSnapshot>, previousElements: Map<string, HTMLElement>): void {
    const currentElements = this.keyedElements()
    const options: KeyframeAnimationOptions = { duration: this.gbFlipDuration, easing: 'ease-in-out' }

    const hasCommonKey = Array.from(currentElements.keys()).some((key) => previousRects.has(key))
    if (!hasCommonKey) return

    currentElements.forEach((element, key) => {
      const previous = previousRects.get(key)
      if (!previous) {
        const running = element.getAnimations?.() ?? []
        if (!running.length) {
          element.animate(
            [
              { opacity: 0, transform: `translateY(${ENTER_TRANSLATE_Y})` },
              { opacity: 1, transform: 'none' },
            ],
            options,
          )
        }
        return
      }

      if (previous.layoutX === element.offsetLeft && previous.layoutY === element.offsetTop) return

      element.getAnimations?.().forEach((animation) => animation.cancel())

      const dx = previous.visual.x - element.offsetLeft
      const dy = previous.visual.y - element.offsetTop
      if (dx || dy) {
        element.animate([{ transform: `translate(${dx}px, ${dy}px)` }, { transform: 'none' }], options)
      }
    })

    previousElements.forEach((element, key) => {
      if (currentElements.has(key)) return
      const previous = previousRects.get(key)
      if (!previous) return
      this.animateExit(element, previous.visual, options)
    })
  }

  private animateExit(element: HTMLElement, rect: FlipRect, options: KeyframeAnimationOptions): void {
    const container = this.el.nativeElement
    const clone = element.cloneNode(true) as HTMLElement
    clone.removeAttribute(FLIP_KEY_ATTRIBUTE)
    clone.style.position = 'absolute'
    clone.style.left = `${rect.x}px`
    clone.style.top = `${rect.y}px`
    clone.style.width = `${rect.width}px`
    clone.style.height = `${rect.height}px`
    clone.style.margin = '0'
    clone.style.pointerEvents = 'none'
    container.appendChild(clone)

    const animation = clone.animate([{ opacity: 1 }, { opacity: 0 }], options)
    animation.onfinish = () => clone.remove()
    animation.oncancel = () => clone.remove()
  }
}
